import React, { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';

// Message types
export type MessageRole = 'user' | 'bot' | 'system';

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
}

// Define predefined responses
export interface PredefinedResponse {
  keywords: string[];
  response: string;
}

// Define intent types
export type ChatIntent = 
  | 'greeting'
  | 'service_inquiry'
  | 'pricing'
  | 'contact'
  | 'booking'
  | 'support'
  | 'about'
  | 'unknown';

interface ChatbotContextType {
  messages: ChatMessage[];
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
  sendMessage: (message: string) => void;
  toggleChat: () => void;
  minimizeChat: () => void;
  maximizeChat: () => void;
  clearChat: () => void;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

// Predefined bot responses
const predefinedResponses: PredefinedResponse[] = [
  {
    keywords: ['hello', 'hi', 'hey', 'greetings'],
    response: "👋 Hello! Welcome to CodemyAI. How can I help you today with your AI needs?"
  },
  {
    keywords: ['services', 'offer', 'solutions', 'provide'],
    response: "We offer a range of AI services including training, business onboarding, workflow automation, custom development, and system optimization. What specific area are you interested in?"
  },
  {
    keywords: ['price', 'pricing', 'cost', 'package', 'fee'],
    response: "Our pricing varies based on your specific needs and project scope. You can get a custom estimate using our pricing calculator. Would you like me to point you to our pricing calculator?"
  },
  {
    keywords: ['contact', 'reach', 'email', 'phone', 'talk'],
    response: "You can reach our team via email at Hello@CodeMyAI.com or phone at +44 07415928896. Would you like to schedule a consultation call instead?"
  },
  {
    keywords: ['consultation', 'meeting', 'book', 'schedule', 'appointment'],
    response: "We'd be happy to schedule a free consultation with one of our AI experts. You can book directly through our calendar system. Would you like me to help you with that?"
  },
  {
    keywords: ['training', 'learn', 'education', 'course', 'workshop'],
    response: "Our AI Training & Education programs are designed for all experience levels. We offer personalized training, workshops, and comprehensive courses. Would you like more information about our training services?"
  },
  {
    keywords: ['automation', 'workflow', 'process'],
    response: "Our Workflow Automation solutions help streamline your business processes with AI. We can automate repetitive tasks, document processing, data analysis, and more. Would you like to see examples of our automation solutions?"
  },
  {
    keywords: ['development', 'custom', 'build', 'create'],
    response: "Our Custom AI Development services include building tailored AI solutions for your specific business needs. From specialized algorithms to complete AI systems, we can create exactly what you need. Would you like to discuss your development requirements?"
  },
  {
    keywords: ['optimization', 'improve', 'enhance', 'tune'],
    response: "Our AI System Optimization services can help improve the performance, accuracy, and efficiency of your existing AI systems. Would you like to learn how we can optimize your AI infrastructure?"
  },
  {
    keywords: ['onboarding', 'integrate', 'implementation', 'adopt'],
    response: "Our Business AI Onboarding services help organizations successfully adopt and integrate AI technologies. We provide comprehensive support throughout the implementation process. Would you like more details about our onboarding approach?"
  }
];

// Intent detection function
const detectIntent = (message: string): ChatIntent => {
  message = message.toLowerCase();
  
  if (/\b(hello|hi|hey|greetings|howdy)\b/.test(message)) {
    return 'greeting';
  }
  
  if (/\b(services|solutions|offer|provide|what.*do.*you.*do)\b/.test(message)) {
    return 'service_inquiry';
  }
  
  if (/\b(price|pricing|cost|package|fee|how.*much|rates)\b/.test(message)) {
    return 'pricing';
  }
  
  if (/\b(contact|reach|email|phone|talk|address)\b/.test(message)) {
    return 'contact';
  }
  
  if (/\b(book|schedule|appointment|meeting|consultation|consultant|expert)\b/.test(message)) {
    return 'booking';
  }
  
  if (/\b(help|support|issue|problem|question|assistance)\b/.test(message)) {
    return 'support';
  }
  
  if (/\b(about|company|who.*are.*you|history|team)\b/.test(message)) {
    return 'about';
  }
  
  return 'unknown';
};

// Get response based on intent
const getResponseForIntent = (intent: ChatIntent): string => {
  switch(intent) {
    case 'greeting':
      return "👋 Hello! Welcome to CodemyAI. How can I help you today with your AI needs?";
    
    case 'service_inquiry':
      return "We offer several AI services:\n\n• AI Training & Education\n• Business AI Onboarding\n• Workflow Automation\n• Custom AI Development\n• AI System Optimization\n\nWhich of these services would you like to know more about?";
    
    case 'pricing':
      return "Our pricing is tailored to your specific needs. You can get a quick estimate using our pricing calculator. Would you like me to direct you there, or would you prefer to discuss your requirements first?";
    
    case 'contact':
      return "You can reach our team via email at Hello@CodeMyAI.com or phone at +44 07415928896. Our office hours are Monday to Friday, 9am to 5pm EST. Would you like to schedule a consultation instead?";
    
    case 'booking':
      return "We'd be happy to schedule a free consultation with one of our AI experts. You can book directly through our calendar system. Would you like me to help you book a consultation now?";
    
    case 'support':
      return "I'll do my best to help! Could you please describe what you need assistance with more specifically?";
    
    case 'about':
      return "CodemyAI is a leading AI solutions provider founded in 2022. We specialize in making AI accessible and practical for businesses of all sizes. Our team of experts has over 50 years of combined experience in AI and machine learning. Would you like to know anything specific about our company?";
    
    case 'unknown':
    default:
      return "I'm not sure I fully understand. Could you please provide more details about what you're looking for? Or you can ask about our services, pricing, or how to contact us.";
  }
};

// Find response based on keywords
const findBestResponse = (message: string): string => {
  // First check for intents (more accurate)
  const intent = detectIntent(message);
  if (intent !== 'unknown') {
    return getResponseForIntent(intent);
  }
  
  // Fall back to keyword matching
  message = message.toLowerCase();
  
  for (const predefined of predefinedResponses) {
    for (const keyword of predefined.keywords) {
      if (message.includes(keyword)) {
        return predefined.response;
      }
    }
  }
  
  // Default response if no matches
  return "I'm not sure I understand. Could you clarify what you're looking for? You can ask about our services, pricing, or contact information.";
};

// Generate unique ID for messages
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

interface ChatbotProviderProps {
  children: ReactNode;
}

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: generateId(),
      role: 'bot',
      content: "👋 Hi there! I'm the CodemyAI assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Ref for timeout ID
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Clear any pending timeouts when unmounting
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Send message as user and get bot response
  const sendMessage = (content: string) => {
    // Add user message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    
    // Simulate bot thinking with a delay
    timeoutRef.current = setTimeout(() => {
      // Get bot response
      const botResponse = findBestResponse(content);
      
      // Add bot message
      const botMessage: ChatMessage = {
        id: generateId(),
        role: 'bot',
        content: botResponse,
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      setIsLoading(false);
    }, 1000); // 1 second delay to seem more natural
  };
  
  // Toggle chat open/closed
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    setIsMinimized(false); // Always show chat when toggling
  };
  
  // Minimize chat
  const minimizeChat = () => {
    setIsMinimized(true);
  };
  
  // Maximize chat
  const maximizeChat = () => {
    setIsMinimized(false);
  };
  
  // Clear chat history
  const clearChat = () => {
    setMessages([
      {
        id: generateId(),
        role: 'bot',
        content: "👋 Hi there! I'm the CodemyAI assistant. How can I help you today?",
        timestamp: new Date(),
      },
    ]);
  };
  
  return (
    <ChatbotContext.Provider value={{
      messages,
      isOpen,
      isMinimized,
      isLoading,
      sendMessage,
      toggleChat,
      minimizeChat,
      maximizeChat,
      clearChat,
    }}>
      {children}
    </ChatbotContext.Provider>
  );
};

// Custom hook to use the chatbot context
export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};
